"use client"

import { Button, InstallSnippet } from "@mdxui/primitives"
import { motion, useReducedMotion } from "framer-motion"
import type { ReactNode } from "react"
import { Check } from 'lucide-react'
import Markdown from "react-markdown"
import { cn } from "../../lib/cn"
import { BEHIND_NAVBAR_CLASSES } from "../../lib/constants"
import {
  gridContainerVariants,
  listItemVariants,
  reducedMotionVariants,
  sectionVariants,
  slideFromLeftVariants,
  slideFromRightVariants,
} from "../../lib/motion"
import { CodeWindowBase } from "../code-window"
import type { InstallSnippetConfig } from "./types"

/**
 * HeroSplitCode block props (split-code hero - content left, code right with gradient overlay)
 *
 * @remarks
 * **CONSTRAINTS FOR AI/PROGRAMMATIC GENERATION:**
 * - Code window ONLY - no image support, NO copy button (gradient overlay covers it)
 * - Uses `theme="auto"` - code window bg uses theme variables (bg-background), not hardcoded colors
 * - CSS mask creates seamless edge fade (works in any theme)
 * - Supports primary/secondary button group above or below bullets
 * - `installSnippet` REPLACES the button group (same substitution rule as
 *   center-announcement — install IS the action; never both)
 * - Best for: hero sections showcasing code examples with feature highlights
 * - Best hero background: beams-center, spotlight-top-left, gradient-blur
 */
export interface HeroSplitCodeProps {
  /** Main heading */
  title?: string
  /** Supporting subtitle */
  subtitle?: string
  /** Feature bullet items with check icons */
  features?: string[]
  /** Primary action button */
  primaryButton?: { text: string; url: string }
  /** Secondary action button */
  secondaryButton?: { text: string; url: string }
  /** Button position relative to features list */
  buttonPosition?: "above" | "below"
  /** Install snippet — REPLACES the button group when present (install is the
   *  action). Positioned by `buttonPosition` like the buttons it replaces. */
  installSnippet?: InstallSnippetConfig
  /** Code to display */
  code?: {
    code: string
    language: string
  }
  /** Optional background pattern (e.g., GradientBlur, GridDashed, etc.) */
  background?: ReactNode
  /**
   * When true, extends the hero section behind the sticky navbar.
   * Uses negative margin to pull section up, with padding to keep content visible.
   * @default true
   */
  behindNavbar?: boolean
  className?: string
}

const defaultCode = `import { Button } from '@mdxui/primitives'
import { Hero } from '@mdxui/neo'

export default function LandingPage() {
  return (
    <Hero
      title="Build faster with MDXUI"
      subtitle="Production-ready components"
      callToAction="Get Started"
    />
  )
}`

const HeroSplitCode = ({
  title = "Build Beautiful Interfaces with MDXUI",
  subtitle = "Type-safe components for building Sites and Apps with MDX. Production-ready and fully accessible.",
  features = [
    "Type-safe components",
    "Dark mode support",
    "Fully accessible",
    "Production ready",
  ],
  primaryButton,
  secondaryButton,
  buttonPosition = "above",
  installSnippet,
  code = { code: defaultCode, language: "typescript" },
  background,
  behindNavbar = true,
  className,
}: HeroSplitCodeProps) => {
  const prefersReducedMotion = useReducedMotion()

  const contentAnimation = prefersReducedMotion
    ? reducedMotionVariants
    : slideFromLeftVariants
  const codeAnimation = prefersReducedMotion
    ? reducedMotionVariants
    : slideFromRightVariants
  const itemAnimation = prefersReducedMotion
    ? reducedMotionVariants
    : listItemVariants

  return (
    <div
      className={cn(
        "relative overflow-hidden",
        behindNavbar && BEHIND_NAVBAR_CLASSES,
        className,
      )}
    >
      {background && <div className="absolute inset-0">{background}</div>}
      <motion.section
        className="relative z-10 overflow-x-hidden py-16 sm:py-24 lg:py-56"
        variants={sectionVariants}
        initial="hidden"
        animate="visible"
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-10 lg:grid-cols-2 lg:items-center">
            {/* Content - Left */}
            <motion.div
              className="w-full min-w-0 max-w-full"
              variants={contentAnimation}
            >
              <h1 className="mb-5 font-semibold text-4xl lg:text-5xl">
                {title}
              </h1>
              <div
                className={cn(
                  "prose prose-base max-w-none text-muted-foreground prose-p:text-muted-foreground prose-strong:text-foreground prose-code:rounded-[var(--radius-sm,calc(var(--radius)*0.5))] prose-code:bg-muted prose-code:px-1.5 prose-code:py-0.5 prose-code:font-normal prose-code:text-foreground prose-code:before:content-none prose-code:after:content-none",
                  buttonPosition === "above" ? "mb-8" : "mb-8 lg:mb-12",
                )}
              >
                <Markdown>{subtitle}</Markdown>
              </div>
              {/* Button group - above (installSnippet replaces the buttons) */}
              {buttonPosition === "above" &&
                (installSnippet || primaryButton || secondaryButton) && (
                  <motion.div
                    className="mb-8 flex max-w-full flex-col gap-3 sm:flex-row lg:mb-12"
                    variants={itemAnimation}
                  >
                    {installSnippet ? (
                      <InstallSnippet
                        package={installSnippet.package}
                        command={installSnippet.command}
                        className="w-full sm:w-auto"
                      />
                    ) : (
                      <>
                        {primaryButton && (
                          <Button asChild className="w-full sm:w-auto">
                            <a href={primaryButton.url}>{primaryButton.text}</a>
                          </Button>
                        )}
                        {secondaryButton && (
                          <Button
                            variant="outline"
                            asChild
                            className="w-full sm:w-auto"
                          >
                            <a href={secondaryButton.url}>{secondaryButton.text}</a>
                          </Button>
                        )}
                      </>
                    )}
                  </motion.div>
                )}
              <motion.ul
                className="grid max-w-xl gap-4 sm:grid-cols-2"
                variants={gridContainerVariants(0.05, 0.1)}
              >
                {features.map((feature, index) => (
                  <motion.li
                    key={String(index)}
                    className="flex min-w-0 items-center gap-2"
                    variants={itemAnimation}
                  >
                    <Check className="size-5 shrink-0 text-primary" />
                    <p className="break-words text-sm font-medium">{feature}</p>
                  </motion.li>
                ))}
              </motion.ul>
              {/* Button group - below (installSnippet replaces the buttons) */}
              {buttonPosition === "below" &&
                (installSnippet || primaryButton || secondaryButton) && (
                  <motion.div
                    className="mt-8 flex max-w-full flex-col gap-3 sm:flex-row lg:mt-12"
                    variants={itemAnimation}
                  >
                    {installSnippet ? (
                      <InstallSnippet
                        package={installSnippet.package}
                        command={installSnippet.command}
                        className="w-full sm:w-auto"
                      />
                    ) : (
                      <>
                        {primaryButton && (
                          <Button asChild className="w-full sm:w-auto">
                            <a href={primaryButton.url}>{primaryButton.text}</a>
                          </Button>
                        )}
                        {secondaryButton && (
                          <Button
                            variant="outline"
                            asChild
                            className="w-full sm:w-auto"
                          >
                            <a href={secondaryButton.url}>{secondaryButton.text}</a>
                          </Button>
                        )}
                      </>
                    )}
                  </motion.div>
                )}
            </motion.div>

            {/* Code Window - Right with CSS mask fade */}
            <motion.div
              className="relative min-w-0 overflow-hidden lg:overflow-visible"
              variants={codeAnimation}
            >
              <CodeWindowBase
                code={code.code}
                language={code.language}
                showHeader={false}
                showCopy={false}
                theme="auto"
                style="borderless"
                className="min-h-[350px] max-h-[450px] w-full rounded-[var(--radius-2xl,calc(var(--radius)*2))] border border-border sm:min-h-[400px] sm:max-h-[500px] lg:max-h-none [mask-image:linear-gradient(to_bottom,black_60%,transparent_100%)] lg:[mask-image:linear-gradient(to_bottom,black_60%,transparent_100%),linear-gradient(to_right,black_70%,transparent_100%)] lg:[mask-composite:intersect] lg:[-webkit-mask-composite:source-in]"
              />
            </motion.div>
          </div>
        </div>
      </motion.section>
    </div>
  )
}

export { HeroSplitCode }
